import { ApiResponse } from "@/interfaces/api-response.interface";
import { api } from "@/lib/api/axios"
import { BudgetLineItemFormData, MovieBudgetResponse } from "@/types/orcamentos.interface"
import { BudgetSubitemFormData } from '@/components/orcamentos/schemas';
import { OrcamentoSubItens } from "./budget.types";

const BUDGET_API_PATH = '/budget'

interface UploadNFResponse {
  url: string;
}

export const budgetService = {
  /**
   * Get all budgets for authenticated user
   */
  getBudgets: () => {
    return api.get(`${BUDGET_API_PATH}/movie-budgets`)
  },

  /**
   * Get budget by movie/project ID
   */
  getBudgetByMovie: (movieId: number) => {
    return api.get<ApiResponse<MovieBudgetResponse>>(`${BUDGET_API_PATH}/movie/${movieId}`)
  },

  /**
   * Get budget table with nested contracts
   */
  getBudgetTable: (budgetId: number) => {
    return api.get(`${BUDGET_API_PATH}/${budgetId}/table`)
  },

  /**
   * Upload fiscal note for a budget
   */
  uploadNFBudget: (payload: FormData, budgetId: number) => {
    return api.post<UploadNFResponse>(`${BUDGET_API_PATH}/upload-nf/${budgetId}`, payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  /**
   * Create a new budget line item
   */
  createLineItem: (budgetId: number, phaseId: string, data: BudgetLineItemFormData & { fiscal_note_url?: string | null }) => {
    const payload = {
      description: data.name,
      total_unity: 1,
      qty_unity: 1,
      qty_item: 1,
      amount: data.value,
      id_movie_resource: 1,
      id_buget_step: parseInt(phaseId),
      payment_expected_till: data.paymentDate,
      fiscal_note_url: data.fiscal_note_url,
      emission_type: data.fiscalDocType,
      emission_number: data.fiscalDocNumber,
    };
    return api.post(`${BUDGET_API_PATH}/${budgetId}/items`, payload)
  },

  /**
   * Create a new budget subitem
   * Excludes fields not applicable to pure subitems like id_movie_resource and payment details
   */
  createSubitem: (budgetId: number, phaseId: string, data: BudgetSubitemFormData) => {
    const payload = {
      description: data.name,
      total_unity: 1,
      qty_unity: 1,
      qty_item: 1,
      amount: data.value,
      id_buget_step: parseInt(phaseId),
    };
    return api.post(`${BUDGET_API_PATH}/${budgetId}/items`, payload)
  },

  /**
   * Update an existing budget line item
   */
  updateLineItem: (budgetId: number, itemId: string, data: BudgetLineItemFormData & { fiscal_note_url?: string | null }) => {
    const payload = {
      description: data.name,
      amount: data.value,
      payment_expected_till: data.paymentDate,
      fiscal_note_url: data.fiscal_note_url
    };
    return api.patch(`${BUDGET_API_PATH}/${budgetId}/items/${itemId}`, payload)
  },

  /**
   * Update an existing budget subitem
   */
  updateSubitem: (budgetId: number, itemId: string, data: BudgetSubitemFormData) => {
    const payload = {
      description: data.name,
      amount: data.value,
    };
    return api.patch(`${BUDGET_API_PATH}/${budgetId}/items/${itemId}`, payload)
  },

  /**
   * Delete a budget line item
   */
  deleteLineItem: (budgetId: number, itemId: number) => {
    return api.delete<{ message: string }>(`${BUDGET_API_PATH}/${budgetId}/items/${itemId}`)
  },

  /**
   * Get current phase of a budget
   */
  getCurrentPhase: (budgetId: number) => {
    return api.get(`${BUDGET_API_PATH}/${budgetId}/current-phase`)
  },

  /**
   * Transition budget to a new phase (1-5)
   */
  transitionPhase: (budgetId: number, newPhase: number) => {
    return api.post(`${BUDGET_API_PATH}/${budgetId}/transition-phase`, { new_phase: newPhase })
  },

  /**
   * Get approval status of a budget
   */
  getApprovalStatus: (budgetId: number) => {
    return api.get(`${BUDGET_API_PATH}/${budgetId}/approval-status`)
  },

  /**
   * Approve a budget
   */
  approveBudget: (budgetId: number, comments?: string) => {
    return api.post(`${BUDGET_API_PATH}/${budgetId}/approve`, { comments })
  },

  /**
   * Reject a budget
   */
  rejectBudget: (budgetId: number, comments?: string) => {
    return api.post(`${BUDGET_API_PATH}/${budgetId}/reject`, { comments })
  },

  findItemsByStep(budgetId: number, stepId: number) {
    return api.get<{ data: OrcamentoSubItens[] }>(`${BUDGET_API_PATH}/${budgetId}/${stepId}/items`)
  }
}